/**
 * Geodesy Utilities
 * Functions for geographic calculations
 */

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in meters
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000.0; // Earth radius in meters
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) ** 2;

    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Convert degrees to radians
 * @param {number} degrees
 * @returns {number} Radians
 */
function toRadians(degrees) {
    return degrees * (Math.PI / 180);
}

/**
 * Calculate total length of a path defined by points
 * @param {Array} points - Array of {lat, lon} objects
 * @returns {number} Total length in meters
 */
export function computePathLength(points) {
    if (!points || points.length < 2) return 0;

    let sum = 0.0;
    for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        sum += haversineDistance(a.lat, a.lon, b.lat, b.lon);
    }

    return sum;
}

/**
 * Thin points by keeping every Nth point
 * @param {Array} points - Array of {lat, lon} objects
 * @param {number} step - Keep every Nth point (1 = keep all)
 * @returns {Array} Thinned array of points
 */
export function thinPoints(points, step) {
    step = Math.max(1, step);
    if (step === 1 || !points || points.length === 0) return points;

    const out = [];
    const n = points.length;

    for (let i = 0; i < n; i += step) {
        out.push(points[i]);
    }

    // Always include the last point if not already included
    if (n > 0 && out[out.length - 1] !== points[n - 1]) {
        out.push(points[n - 1]);
    }

    return out;
}

/**
 * Format distance in meters to kilometers with specified decimals
 * @param {number} meters - Distance in meters
 * @param {number} decimals - Number of decimal places (default: 3)
 * @returns {number} Distance in kilometers
 */
export function metersToKilometers(meters, decimals = 3) {
    return parseFloat((meters / 1000.0).toFixed(decimals));
}

/**
 * Format number with thousand separators
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number
 */
export function formatNumber(num, decimals = 2) {
    if (num === null || num === undefined) return '';
    return parseFloat(num).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
}
